import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import LineSightseeingIcon from 'components/Luxkit/Icons/line/LineSightseeingIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useBedbankSelectDates from 'hooks/OfferPage/useBedbankSelectDates'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BedbankRoomOptionRate from './BedbankRoomOptionRate'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const INITIAL_RATES_COUNT_TO_DISPLAY = 4

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView;
  fetchingRates?: boolean;
  isFilterActive: boolean;
  isHighlighted?: boolean;
  roomListId: string;
  onViewPackageOptions: () => void;
  roomPosition: number;
  source?: string;
}

function BedbankRoomOption(props: Props) {
  const {
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    fetchingRates,
    isFilterActive,
    isHighlighted = false,
    roomListId,
    onViewPackageOptions,
    roomPosition,
    source,
  } = props

  const { value: showingMoreRates, toggle: showMoreRates } = useToggle(false)

  const rates = packageView.rates
  const promotions = packageView.promotions
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])

  const isInitialPackageOption = promotions[rates[0]?.id]?.id === rates[0]?.id

  const canBuyNow = packageView.available && packageView.capacityValid && packageView.show && !fetchingRates

  // use full rates as dep so when user expand/hide rates the list id stay the same
  const rateListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rates, canBuyNow])

  const hasPromotions = offer.promotions?.length > 0

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const ratesToDisplay = useMemo(() => {
    if (canBuyNow) {
      if (showingMoreRates) {
        return rates
      } else {
        return take(rates, INITIAL_RATES_COUNT_TO_DISPLAY)
      }
    }
    return []
  }, [canBuyNow, showingMoreRates, rates])

  const validCheckinCheckout = !!(checkInDate && checkOutDate)
  const hotelAvailable = packageView.available
  const canPurchase = rates.length > 0 && packageView.capacityValid && hotelAvailable && !fetchingRates

  const roomTileActionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (fetchingRates) {
      return 'loading'
    }
    if (!canPurchase) {
      if (validCheckinCheckout && packageView.capacityValid) {
        return 'sold-out-on-dates'
      }
      if (hotelAvailable && !packageView.capacityValid) {
        return 'exceeded-capacity'
      }
    }
    if (validCheckinCheckout && !hotelAvailable && packageView.capacityValid) {
      return 'sold-out'
    }
    if (!isFilterActive && rates.length == 0 && checkInDate && checkOutDate) {
      return 'sold-out'
    }
    if (hotelAvailable && !packageView.capacityValid) {
      return 'exceeded-capacity'
    }
    if (hotelAvailable && packageView.capacityValid && !validCheckinCheckout) {
      return 'available-on-dates'
    }
  }, [canPurchase, checkInDate, checkOutDate, fetchingRates, hotelAvailable, isFilterActive, packageView.capacityValid, rates.length, validCheckinCheckout])

  const offerPageDispatch = useOfferPageDispatch()

  const handleAddAnotherUnitClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleChangeDatesClick = useBedbankSelectDates(packageView.package.roomId)
  const handleSelectDates = useBedbankSelectDates(packageView.package.roomId)

  const handleCheckAvailabilityClick = useCallback(() => {
    handleSelectDates()
    Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: packageView.package.roomId }))
  }, [handleSelectDates, offer, packageView.package.roomId])

  return <Pane border="neutral-five" borderRadius="S" data-testid="PackageOption-bedbank">
    {isHighlighted && <MessageBanner
      kind="promotional-primary"
      description={<b>You were interested in this option</b>}
      icon={<LineSightseeingIcon />}
    />}
    <ScrollAnchor id={packageView.package.roomId} vOffset={rem(80)} />
    <AccommodationRoomTile roomPackage={packageView.package}>
      <AccommodationRoomTileImage roomPackage={packageView.package} />
      <AccommodationRoomTileDetails
        roomPackage={packageView.package}
        urgencyLabels={<>
          {hasPromotions && <BookingsCountLabel
            bookings={packageView.package.stats.booked}
            threshold="room"
          />}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        actions={<CSSBreakpoint min="desktop">
          <TripGuard>
            <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
          </TripGuard>
        </CSSBreakpoint>}
      >
        <AccommodationRoomTileActions
          roomPackage={packageView.package}
          state={roomTileActionsState}
          onAddAnotherUnitClick={handleAddAnotherUnitClick}
          onChangeDatesClick={handleChangeDatesClick}
          onCheckAvailabilityClick={handleCheckAvailabilityClick}
        />
      </AccommodationRoomTileDetails>
    </AccommodationRoomTile>
    <RateSection data-testid="rate-options">
      {ratesToDisplay.map((rate, i) => <BedbankRoomOptionRate
        key={rate.id}
        rate={rate}
        offer={offer}
        roomListId={roomListId}
        packageView={packageView}
        optionNumber={i + 1}
        isInitialPackageOption={isInitialPackageOption}
        rateListId={rateListId}
        roomPosition={roomPosition}
        source={source}
      />)}
      {rates.length > INITIAL_RATES_COUNT_TO_DISPLAY && <ShowMoreContent variant="medium" align="center">
        <SmallToggleButton
          on={showingMoreRates}
          onClick={showMoreRates}
        >
          {showingMoreRates ? 'Show less options' : 'Show all options'}
        </SmallToggleButton>
      </ShowMoreContent>}
    </RateSection>
  </Pane>
}

type MappedProps = Pick<Props, 'isFilterActive' | 'checkInDate' | 'checkOutDate' >

export default withOfferPageAndReduxState((offerState): MappedProps => ({
  isFilterActive: !!offerState.activeFilter,
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
}), BedbankRoomOption)
